import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import ThemeToggle from '../ui/ThemeToggle'
import { useResolvedTheme } from '../hooks/useResolvedTheme'
import { site } from '../content/site'
import logoLight from '../assets/logo.png'
import logoDark from '../assets/logo-dark.png'
import styles from './Header.module.css'

export default function Header() {
    /* One <img> whose source follows the theme, rather than two hidden behind
       CSS: the artwork is ~16KB a side, and only the visible one should be
       fetched. <picture media=...> cannot be used because it sees the OS
       preference but not the manual override. */
    const theme = useResolvedTheme()
    const [menuOpen, setMenuOpen] = useState(false)
    const location = useLocation()
    const firstLink = useRef<HTMLAnchorElement>(null)

    // navigating is the end of the menu's job
    useEffect(() => setMenuOpen(false), [location.pathname])

    // Escape closes it, matching every other dismissible surface on the site
    useEffect(() => {
        if (!menuOpen) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
        window.addEventListener('keydown', onKey)
        firstLink.current?.focus()
        return () => window.removeEventListener('keydown', onKey)
    }, [menuOpen])

    return (
        <header className={styles.header}>
            <NavLink to="/" className={styles.brand} aria-label={`${site.name} home`}>
                <img
                    className={styles.logo}
                    src={theme === 'dark' ? logoDark : logoLight}
                    alt={site.name}
                    width={170}
                    height={83}
                />
            </NavLink>

            <button
                type="button"
                className={styles.toggler}
                aria-expanded={menuOpen}
                aria-controls="main-nav"
                onClick={() => setMenuOpen((open) => !open)}
            >
                <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
                <span className="visually-hidden">{menuOpen ? 'Close menu' : 'Open menu'}</span>
            </button>

            <nav
                id="main-nav"
                className={menuOpen ? styles.navOpen : styles.nav}
                aria-label="Main"
            >
                {site.nav.map((item, i) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/'}
                        ref={i === 0 ? firstLink : undefined}
                        className={({ isActive }) => [
                            'primary' in item && item.primary ? styles.primary : '',
                            isActive ? styles.linkActive : styles.link,
                        ].filter(Boolean).join(' ')}
                    >
                        {item.label}
                    </NavLink>
                ))}
            </nav>

            <div className={styles.theme}>
                <ThemeToggle />
            </div>
        </header>
    )
}
